import React, { useState } from "react"
import { useParams } from "react-router-dom"
import pluralize from "pluralize"
import {
  Flex, Text, HStack, Box,
} from "@chakra-ui/react"
import Link from "components/elements/Link"
import { useKeyPress } from "ahooks"
import useGuestsStore from "services/useGuestsStore"
import { useSegmentSizes } from "queries/events"
import { useShallow } from "zustand/shallow"
import {
  EventCounts, EventGroup, Segment, Table,
} from "sharedTypes"
import usePermissions from "services/permissions"
import useFeatureFlag from "services/featureFlags"
import Action, { Actions } from "../../MultipleSelectActions/Action"
import { selectedFilterCount } from "../../ScrollView/Filters"
import ActionIcon from "./ActionIcon"
import SelectAllLink from "../../SelectAllLink"

type Props = {
  eventGroups: EventGroup[]
  segments: Segment[]
  counts: EventCounts
  tables: Table[]
  onCancel: () => void
  onSelectAll: () => void
}

const MultipleSelectActions = ({
  eventGroups,
  segments,
  counts,
  tables,
  onCancel,
  onSelectAll,
}: Props) => {
  const [action, setAction] = useState<Actions | null>(null)
  const { canViewBadges } = usePermissions()
  const { badgeBuilder: badgeBuilderEnabled } = useFeatureFlag()
  const {
    selectedGuestIds, allSelected, groupId, segmentId,
  } = useGuestsStore(
    useShallow((state) => ({
      selectedGuestIds: state.selectedGuestIds,
      allSelected: state.allSelected,
      groupId: state.groupId,
      segmentId: state.segmentId,
    })),
  )

  const { eventId } = useParams<{ eventId: string }>()
  const segmentSizes = useSegmentSizes(eventId)

  let count
  if (allSelected) {
    count = selectedFilterCount(
      eventGroups,
      counts,
      segments,
      segmentId,
      groupId,
      segmentSizes,
    )
  } else {
    count = selectedGuestIds.length
  }

  useKeyPress("esc", () => onCancel())

  const handleActionClick = (actionType: Actions) => {
    setAction(actionType)
  }

  return (
    <>
      <Box
        position="relative"
        width="100%"
        height="100%"
        bg="gray.50"
        overflow="hidden"
        h={16}
      >
        <Flex
          justify="space-between"
          align="center"
          h="full"
          width="100%"
          px={8}
        >
          <Flex align="center" flexShrink={0}>
            <Text
              fontWeight="bold"
              fontSize="lg"
              mr={4}
              data-testid="guest-number-counter-text"
            >
              {count === null ? "All matching guests selected" : `${count} ${pluralize("guest", count)} selected`}
            </Text>
            <HStack spacing={4}>
              {!allSelected && (
                <SelectAllLink fontSize="md" onClick={onSelectAll} />
              )}
              <Link fontSize="md" onClick={onCancel}>
                cancel
              </Link>
            </HStack>
          </Flex>

          <HStack spacing={2}>
            <ActionIcon
              icon="circle"
              text="Status"
              onClick={() => handleActionClick("change-status")}
            />
            <ActionIcon
              icon="tag"
              text="Tag"
              onClick={() => handleActionClick("tag-guest-list")}
            />
            <ActionIcon
              icon="invite-seat"
              text="Seat"
              onClick={() => handleActionClick("assign-seat")}
            />
            <ActionIcon
              icon="email-only-large"
              text="Email"
              onClick={() => handleActionClick("send-email")}
            />
            <ActionIcon
              icon="smart-reminder"
              text="Reminder"
              onClick={() => handleActionClick("send-smart-reminder")}
            />
            {canViewBadges && badgeBuilderEnabled && (
              <ActionIcon
                icon="badges"
                text="Print"
                onClick={() => handleActionClick("print-badge")}
              />
            )}
            <ActionIcon
              icon="times"
              text="Remove"
              onClick={() => handleActionClick("remove-from-event")}
              color="red.500"
            />
          </HStack>
        </Flex>
      </Box>

      <Action
        tables={tables}
        action={action}
        onClose={() => setAction(null)}
        selectedCount={count}
      />
    </>
  )
}

export default MultipleSelectActions
